// Billing Page
// -------------
// Account-owner-only surface for plan + app-license management. Reached from the
// Admin dropdown; route-gated in AppRoutes via OwnerGate (CLIENT_OWNER_GROUP)
// and nav-gated in Menubar (isOwner). Sys Admins and Pentesters cannot reach it.
//
// Layout, top to bottom:
//   1. How app licenses work (explainer) — page-level, because it explains
//      every box below it, not just one.
//   2. Buy app licenses.
//   3. Payment method — the card the subscriptions below are charged to.
//   4. Your subscriptions — one row per PURCHASE, which is the unit Stripe
//      bills and therefore the unit that can be cancelled.
//   5. Available licenses — bought, not yet attached to an app. Above Licensed
//      apps because it answers "can I add an app right now?".
//   6. Licensed apps      — licenses attached to an app.
//   7. Apps without a license — an app whose license lapsed, or (rarely) one
//      never licensed. Actionable: attach a spare license to put it back in
//      service.
//   8. Invoices — receipts, last because it is reference rather than action.
//
// 5 and 6 used to be one list, which mixed "your app and its allowance" with
// "a license waiting to be used" — two different things a reader has to do
// two different things about.
//
// Everything that groups licenses keys off LIVE ones. A cancelled subscription
// leaves its slots inactive but still pointing at their app, so grouping on
// "has a license" would file a lapsed app under Licensed apps and never surface
// the one app the owner actually has to act on.

import React, { useMemo, useState, useEffect, useCallback } from "react";
import { AlertTriangle, Ticket, Loader2, Minus, Plus, Globe, CreditCard, Receipt, ExternalLink, Download } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTenantUsage } from "@/utils/useTenantUsage";
import { useTenantEntitlements } from "@/utils/useTenantEntitlements";
import { TenantEntitlement } from "@/services/tenantEntitlements.service";
import {
  fetchPurchasablePlans,
  initiateEntitlementCheckout,
  checkoutErrorMessage,
  formatAnnualSavings,
  formatOrderSummary,
  formatPerAppPrice,
  formatPlanAmount,
  planPresentation,
  PurchasablePlan,
} from "@/services/stripeCheckout.service";
import {
  assignLicenseToWebApplication,
  assignLicenseErrorMessage,
} from "@/services/licenseAssignment.service";
import {
  fetchBillingSubscriptions,
  cancelLicenseSubscription,
  resumeLicenseSubscription,
  subscriptionChangeErrorMessage,
  fetchBillingOverview,
  fetchBillingInvoices,
  createPaymentMethodSetupSession,
  paymentMethodErrorMessage,
  formatPaymentMethod,
  formatCardExpiry,
  isCardExpiringSoon,
  LicenseSubscriptionGroup,
  BillingOverview,
  BillingInvoice,
} from "@/services/billingSubscriptions.service";
import BuyLicensesModal from "@/components/billing/BuyLicensesModal";
import CancelLicensesModal from "@/components/billing/CancelLicensesModal";
import { ENTITLEMENTS_UPDATE, eventBus } from "@/services/eventBus.service";
import { formatShortDate } from "@/utils/date";
import HelpTip from "@/components/common/HelpTip";
import Explainer from "@/components/common/Explainer";
import WebApplicationClass from "@/models/webApplication";

// Client-side quantity bounds mirror the BE cap (ENTITLEMENT_TOPUP_MAX_QUANTITY).
const MIN_PURCHASE_QUANTITY = 1;
const MAX_PURCHASE_QUANTITY = 20;

interface BillingPageProps {
  // Used to resolve a license's bound web-app id to its display name, and to
  // find apps that no license points at.
  listOfWebapps: WebApplicationClass[];
}

type StatusTone = "muted" | "warn" | "ok";

const TONE_CLASS: Record<StatusTone, string> = {
  muted: "text-gray-400",
  warn: "text-amber-600",
  ok: "text-gray-500",
};

// One line describing where a license stands, tuned per plan type + status.
function licenseStatusLabel(entitlement: TenantEntitlement): {
  text: string;
  tone: StatusTone;
} {
  if (entitlement.status === "inactive") {
    return { text: "Inactive", tone: "warn" };
  }
  // A one-off whose two pentests are spent. Previously fell through to the
  // one-off branch below and rendered "Access ends {date}", which reads as
  // still usable when in fact nothing more can be run on it.
  if (entitlement.status === "consumed_locked") {
    return { text: "Fully used", tone: "warn" };
  }
  if (entitlement.status === "unbound") {
    return { text: "Not yet assigned to an app", tone: "muted" };
  }
  if (entitlement.planType === "one_off") {
    const expiresAt = entitlement.accessExpiresAt;
    if (expiresAt == null) {
      return { text: "One-off access", tone: "muted" };
    }
    const expired = Math.floor(Date.now() / 1000) > expiresAt;
    return expired
      ? { text: `Access ended ${formatShortDate(expiresAt)}`, tone: "warn" }
      : { text: `Access ends ${formatShortDate(expiresAt)}`, tone: "ok" };
  }
  // Subscription slot, live. The pentest allowance is monthly for every
  // subscription — an annual plan is just the monthly allowance paid a year
  // ahead (N pentests per month, not per year) — and it resets on THIS
  // subscription's own anniversary day, so two slots bought on different dates
  // show different dates. Says "resets", not "renews": on the billing page
  // "renews" reads as a charge, and this date is the allowance refill.
  //
  // The BE derives a date for every subscription slot, falling back to the 1st
  // of next month when it cannot resolve an anchor, so the dateless branch
  // should be unreachable. It says "on renewal" rather than the older
  // "monthly", which read like a date had failed to load.
  return entitlement.periodEndsAt
    ? { text: `Active · allowance resets ${formatShortDate(entitlement.periodEndsAt)}`, tone: "ok" }
    : { text: "Active · allowance resets on renewal", tone: "ok" };
}

// Pentests left, phrased as remaining rather than used. "Remaining" is the
// house style everywhere else (PentestCard, Applications) and it answers the
// question the reader actually has.
function allowanceLabel(entitlement: TenantEntitlement): string {
  const remaining = Math.max(0, entitlement.testsAllowed - entitlement.testsUsedThisPeriod);
  const planLabel = entitlement.planType === "one_off" ? "One-off" : "Subscription";
  return `${planLabel} · ${remaining} of ${entitlement.testsAllowed} pentest${
    entitlement.testsAllowed === 1 ? "" : "s"
  } remaining`;
}

export default function BillingPage({ listOfWebapps }: BillingPageProps) {
  // usage drives the tenant-tier filter for purchasable plans (not displayed;
  // customers already know their tier).
  const { usage } = useTenantUsage();
  // No refresh() here: post-purchase refetches go out as an ENTITLEMENTS_UPDATE
  // broadcast so this page and every other entitlement surface re-read together.
  const { entitlements, isLoading: licensesLoading, error } =
    useTenantEntitlements();

  const appNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const app of listOfWebapps || []) {
      if (app?.id) map.set(app.id, app.name || app.id);
    }
    return map;
  }, [listOfWebapps]);

  // The groups the page renders. Split here rather than at render time so each
  // box has one clear meaning.
  //
  // Everything keys off LIVE licenses, not merely bound ones. A cancelled
  // subscription leaves its slots inactive but still pointing at their app, so
  // grouping on boundWebAppId alone kept a lapsed app filed under "Licensed
  // apps" (marked Inactive) and never surfaced it as needing attention — which
  // is exactly the app the owner has to act on now that they can re-license it.
  const { licensedApps, availableLicenses, appsWithoutLicense, lapsedAppIds } =
    useMemo(() => {
      const live = entitlements.filter(
        (entitlement) => entitlement.status !== "inactive"
      );
      const bound = live.filter((entitlement) => entitlement.boundWebAppId);
      const licensedAppIds = new Set(
        bound.map((entitlement) => entitlement.boundWebAppId as string)
      );
      // Apps whose licence ended and has not been replaced. Used only to explain
      // WHY an app is unlicensed — a lapsed app and a never-licensed one need
      // the same action but read very differently to the person fixing it.
      const lapsed = new Set(
        entitlements
          .filter(
            (entitlement) =>
              entitlement.status === "inactive" &&
              entitlement.boundWebAppId &&
              !licensedAppIds.has(entitlement.boundWebAppId)
          )
          .map((entitlement) => entitlement.boundWebAppId as string)
      );
      return {
        licensedApps: bound,
        availableLicenses: live.filter((entitlement) => !entitlement.boundWebAppId),
        appsWithoutLicense: (listOfWebapps || []).filter(
          (app) => app?.id && !licensedAppIds.has(app.id)
        ),
        lapsedAppIds: lapsed,
      };
    }, [entitlements, listOfWebapps]);

  // --- Purchase (buy more app licenses) ---
  const [plans, setPlans] = useState<PurchasablePlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [buyingPlanId, setBuyingPlanId] = useState<string | null>(null);
  const [buyError, setBuyError] = useState<string | null>(null);
  const [checkout, setCheckout] =
    useState<{ clientSecret: string; publishableKey: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchPurchasablePlans()
      .then((fetched) => {
        if (!cancelled) {
          setPlans(fetched);
          setPlansLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPlansError("Couldn't load available plans. Please try again later.");
          setPlansLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Show only the plans for this tenant's tier; fall back to all active plans if
  // the tier can't be resolved yet (so the panel is never wrongly empty).
  const tenantTier = (usage?.tierLabel || "").toLowerCase().includes("enterprise")
    ? "enterprise"
    : "sme";
  const purchasablePlans = useMemo(() => {
    const forTier = plans.filter(
      (plan) => (plan.tier || "").toLowerCase() === tenantTier
    );
    return forTier.length > 0 ? forTier : plans;
  }, [plans, tenantTier]);

  // Spare licenses collapsed to one row per plan, with a count. Grouped on the
  // plan's own display label so the box reads in the same terms as the Buy box
  // above it.
  //
  // The label is resolved by joining each license's planId back to the plan
  // catalog. A license bought under a plan that is no longer purchasable (or a
  // tenant-specific plan that never was) will not match, so it falls back to
  // its plan type rather than showing a raw plan id.
  // Each group also carries its members' ids, because attaching a license to an
  // app needs a specific one: when a tenant holds spares of more than one plan,
  // "attach a license" is ambiguous and the owner must say which — a
  // Point-in-Time and a Continuous license cover the same app very differently.
  const availableLicenseGroups = useMemo(() => {
    const groups = new Map<
      string,
      { key: string; label: string; count: number; testsAllowed: number; entitlementIds: string[] }
    >();
    for (const license of availableLicenses) {
      const matchingPlan = plans.find((plan) => plan.planId === license.planId);
      const label = matchingPlan
        ? planPresentation(matchingPlan).label
        : license.planType === "one_off"
        ? "Point-in-Time"
        : "Continuous";
      const existing = groups.get(label);
      if (existing) {
        existing.count += 1;
        existing.entitlementIds.push(license.id);
      } else {
        groups.set(label, {
          key: label,
          label,
          count: 1,
          testsAllowed: license.testsAllowed,
          entitlementIds: [license.id],
        });
      }
    }
    return Array.from(groups.values()).sort((first, second) =>
      first.label.localeCompare(second.label)
    );
  }, [availableLicenses, plans]);

  const quantityFor = (planId: string): number => quantities[planId] ?? MIN_PURCHASE_QUANTITY;
  // Every write goes through this clamp, so quantity can never leave
  // [MIN, MAX] — the stepper buttons are the only writers and they are also
  // disabled at the bounds, which makes 0 or a negative unreachable by
  // construction rather than merely corrected after the fact.
  const setQuantityFor = (planId: string, value: number): void => {
    const clamped = Math.max(
      MIN_PURCHASE_QUANTITY,
      Math.min(MAX_PURCHASE_QUANTITY, Math.floor(value) || MIN_PURCHASE_QUANTITY)
    );
    setQuantities((prev) => ({ ...prev, [planId]: clamped }));
  };

  // Annual is priced as a discount on twelve monthly payments, so the saving is
  // only meaningful next to the monthly plan of the SAME tier. Both come from
  // the live Stripe prices, so this stays correct through a repricing.
  const annualSavingsLabel = useMemo(() => {
    const monthlyPlan = purchasablePlans.find((plan) => plan.interval === "month");
    const annualPlan = purchasablePlans.find((plan) => plan.interval === "year");
    return formatAnnualSavings(monthlyPlan, annualPlan);
  }, [purchasablePlans]);

  const handleBuy = useCallback(
    async (planId: string, quantity: number) => {
      setBuyError(null);
      setBuyingPlanId(planId);
      try {
        const session = await initiateEntitlementCheckout(planId, quantity);
        if (session.error || !session.clientSecret || !session.publishableKey) {
          setBuyError(checkoutErrorMessage(session.error));
          return;
        }
        setCheckout({
          clientSecret: session.clientSecret,
          publishableKey: session.publishableKey,
        });
      } catch {
        setBuyError(checkoutErrorMessage(null));
      } finally {
        setBuyingPlanId(null);
      }
    },
    []
  );

  // Provisioning is async via the Stripe webhook, so nudge the license list a
  // few times after payment rather than expecting it to be instant.
  //
  // Broadcast rather than calling refresh() directly: this page's own hook is
  // one of several instances, and each one writes the fresh list back to the
  // cache. That's what lets a user pay here and walk straight into Add App
  // with the new slot already selectable, instead of seeing the pre-purchase
  // list until something happens to refetch.
  const handleCheckoutComplete = useCallback(() => {
    eventBus.emit(ENTITLEMENTS_UPDATE);
    [2000, 5000, 9000].forEach((delayMs) =>
      setTimeout(() => eventBus.emit(ENTITLEMENTS_UPDATE), delayMs)
    );
  }, []);

  // Closing the modal is not proof the user didn't pay — Stripe's onComplete
  // can be missed (tab closed, network blip), so re-read rather than assume.
  const handleCheckoutClose = useCallback(() => {
    setCheckout(null);
    eventBus.emit(ENTITLEMENTS_UPDATE);
  }, []);

  // --- Account-level billing context, card on file, receipts ---
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);
  const [cardSetup, setCardSetup] =
    useState<{ clientSecret: string; publishableKey: string } | null>(null);
  const [cardSetupPending, setCardSetupPending] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);

  const loadOverview = useCallback(async () => {
    try {
      setOverview(await fetchBillingOverview());
    } catch {
      // Non-fatal: the rest of the page is still correct without the card
      // summary. The one field that must never be guessed — the data-retention
      // window — falls back to vaguer copy in the closure confirmation rather
      // than to an invented number.
      setOverview(null);
    }
  }, []);

  useEffect(() => {
    loadOverview();
    let cancelled = false;
    fetchBillingInvoices()
      .then((fetched) => {
        if (cancelled) return;
        setInvoices(fetched);
        setInvoicesLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setInvoicesError("Couldn't load your invoices. Please try again shortly.");
        setInvoicesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadOverview]);

  const handleUpdateCard = useCallback(async () => {
    setCardSetupPending(true);
    setCardError(null);
    try {
      const session = await createPaymentMethodSetupSession();
      if (session.error || !session.clientSecret || !session.publishableKey) {
        setCardError(paymentMethodErrorMessage(session.error));
        return;
      }
      setCardSetup({
        clientSecret: session.clientSecret,
        publishableKey: session.publishableKey,
      });
    } catch {
      setCardError(paymentMethodErrorMessage(null));
    } finally {
      setCardSetupPending(false);
    }
  }, []);

  // The new card is promoted to default by the webhook, so it isn't readable
  // back instantly — nudge the overview a few times rather than showing the old
  // card as though nothing happened.
  const handleCardSetupComplete = useCallback(() => {
    [1000, 3000, 6000].forEach((delayMs) => setTimeout(loadOverview, delayMs));
  }, [loadOverview]);

  const cardLabel = formatPaymentMethod(overview?.paymentMethod ?? null);
  const cardExpiry = formatCardExpiry(overview?.paymentMethod ?? null);
  const cardExpiringSoon = isCardExpiringSoon(overview?.paymentMethod ?? null);

  // --- Subscriptions (purchase groups) ---
  // One group = one purchase = one Stripe subscription covering N licenses.
  // This is what can be cancelled, because it is what Stripe bills.
  const [subscriptionGroups, setSubscriptionGroups] = useState<LicenseSubscriptionGroup[]>([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(true);
  const [subscriptionsError, setSubscriptionsError] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<LicenseSubscriptionGroup | null>(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [resumingGroupId, setResumingGroupId] = useState<string | null>(null);
  const [changeNotice, setChangeNotice] = useState<string | null>(null);

  const loadSubscriptions = useCallback(async () => {
    try {
      const groups = await fetchBillingSubscriptions();
      setSubscriptionGroups(groups);
      setSubscriptionsError(null);
    } catch {
      // Show an error rather than an empty list: "you have no subscriptions" is
      // a very different statement from "we couldn't load them", and the wrong
      // one would tell an owner their licenses had vanished.
      setSubscriptionsError("Couldn't load your subscriptions. Please try again shortly.");
    } finally {
      setSubscriptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSubscriptions();
    // Cancellation changes what the licence list means, so re-read together
    // with every other entitlement surface.
    eventBus.on(ENTITLEMENTS_UPDATE, loadSubscriptions);
    return () => {
      eventBus.off(ENTITLEMENTS_UPDATE, loadSubscriptions);
    };
  }, [loadSubscriptions]);

  // Cancelling every licence in a group only closes the account if nothing else
  // is live anywhere — another subscription, or a one-off licence, keeps it
  // open. The BE re-derives this authoritatively; this is for the confirmation
  // copy shown BEFORE the call.
  const wouldCloseAccountIfGroupFullyCancelled = useCallback(
    (group: LicenseSubscriptionGroup): boolean => {
      const liveElsewhere = entitlements.some(
        (entitlement) =>
          entitlement.status !== "inactive" &&
          !(group.entitlementIds || []).includes(entitlement.id)
      );
      return !liveElsewhere;
    },
    [entitlements]
  );

  const handleCancelLicenses = useCallback(
    async (entitlementIds: string[]) => {
      if (!cancelTarget) return;
      setCancelSubmitting(true);
      setCancelError(null);
      try {
        const result = await cancelLicenseSubscription(
          cancelTarget.licenseGroupId,
          entitlementIds
        );
        if (result.error) {
          setCancelError(subscriptionChangeErrorMessage(result.error));
          return;
        }
        setCancelTarget(null);
        setChangeNotice(
          result.closesAccount
            ? `Your account will close on ${
                result.cancelsAt ? formatShortDate(result.cancelsAt) : "the end of the period"
              }. You can undo this until then.`
            : `${result.cancelledCount} license${
                result.cancelledCount === 1 ? "" : "s"
              } will end on ${
                result.cancelsAt ? formatShortDate(result.cancelsAt) : "the end of the period"
              }. You can undo this until then.`
        );
        eventBus.emit(ENTITLEMENTS_UPDATE);
      } catch {
        setCancelError(subscriptionChangeErrorMessage(null));
      } finally {
        setCancelSubmitting(false);
      }
    },
    [cancelTarget]
  );

  const handleResumeSubscription = useCallback(async (group: LicenseSubscriptionGroup) => {
    setResumingGroupId(group.licenseGroupId);
    setSubscriptionsError(null);
    try {
      const result = await resumeLicenseSubscription(group.licenseGroupId);
      if (result.error) {
        setSubscriptionsError(subscriptionChangeErrorMessage(result.error));
        return;
      }
      setChangeNotice("Your subscription has been restored. Nothing will be cancelled.");
      eventBus.emit(ENTITLEMENTS_UPDATE);
    } catch {
      setSubscriptionsError(subscriptionChangeErrorMessage(null));
    } finally {
      setResumingGroupId(null);
    }
  }, []);

  // --- Attach a license to an existing app ---
  // The recovery path for an app whose license lapsed. Per-app state (rather
  // than one page-level flag) so an error on one app doesn't blank the controls
  // on the others, and two apps can't look busy because one is.
  const [assigningAppId, setAssigningAppId] = useState<string | null>(null);
  const [assignErrorByAppId, setAssignErrorByAppId] = useState<Record<string, string>>({});
  // Which license plan to attach, per app. Defaults to the first available
  // group; only rendered as a choice when there is more than one.
  const [chosenLicenseKeyByAppId, setChosenLicenseKeyByAppId] = useState<Record<string, string>>({});

  const handleAssignLicense = useCallback(
    async (webApplicationId: string, entitlementId: string | null) => {
      setAssigningAppId(webApplicationId);
      setAssignErrorByAppId((previous) => {
        const { [webApplicationId]: _cleared, ...rest } = previous;
        return rest;
      });
      try {
        const result = await assignLicenseToWebApplication(webApplicationId, entitlementId);
        if (result.error) {
          setAssignErrorByAppId((previous) => ({
            ...previous,
            [webApplicationId]: assignLicenseErrorMessage(result.error),
          }));
          return;
        }
        // Broadcast rather than refresh() locally: every entitlement surface
        // (Applications rows, the Pentest gate) must see the app become
        // testable, not just this page.
        eventBus.emit(ENTITLEMENTS_UPDATE);
      } catch {
        setAssignErrorByAppId((previous) => ({
          ...previous,
          [webApplicationId]: assignLicenseErrorMessage(null),
        }));
      } finally {
        setAssigningAppId(null);
      }
    },
    []
  );

  const licensesPending = !error && licensesLoading && entitlements.length === 0;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-50">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">

        <div className="mb-4">
          <h1 className="text-2xl font-semibold text-gray-900">Billing</h1>
          <p className="mt-1 text-sm text-gray-500">
            Your plan and app licenses.
          </p>
        </div>

        {/* Page-level explainer: it describes the model the whole page is built
            on, so it sits above every box rather than inside one of them. */}
        <div className="mb-6">
          <Explainer label="how app licenses work">
            <div className="flex flex-col gap-2">
              <p>
                Each web app you pentest is covered by one{" "}
                <span className="font-medium text-gray-700">license</span>, which
                grants a number of pentests per period on the app it's assigned to.
              </p>
              <ul className="flex list-disc flex-col gap-1.5 pl-4">
                <li>
                  <span className="font-medium text-gray-700">Point-in-Time</span> — a
                  fixed access window (it ends on the date shown) plus its pentest
                  allowance for that window.
                </li>
                <li>
                  {/* "each billing period" was wrong for an annual plan: the
                      allowance is monthly on every subscription — annual is the
                      monthly allowance paid a year ahead, not a yearly bundle. */}
                  <span className="font-medium text-gray-700">Continuous</span> —
                  renews its pentest allowance each month and doesn't expire,
                  whether you're billed monthly or annually.
                </li>
                <li>
                  <span className="font-medium text-gray-700">Not yet assigned</span>{" "}
                  — a license you've bought but not attached to an app. You attach it
                  when you set an app up, or from this page to an app whose license
                  has ended.
                </li>
              </ul>
              {/* Deliberately does NOT mention the billable-duration rule. It
                  is operational detail the customer doesn't need while reading
                  about licenses; where it IS decision-relevant (the Stop Test
                  confirmation, where "stop now and it's free" changes what the
                  user does) it is stated there instead. */}
              <p>Running a pentest uses one from the app's allowance.</p>
            </div>
          </Explainer>
        </div>

        {/* Buy app licenses — Client Owner only (the whole page is owner-gated;
            the BE also enforces the ClientOwner role on the mutation). Each
            purchase opens Stripe's embedded checkout; licenses appear in the
            list below once the payment webhook provisions them. */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="mb-4 flex items-center gap-2">
              <h2 className="text-sm font-semibold text-gray-900">Buy app licenses</h2>
              <HelpTip
                label="How buying licenses works"
                content="Pick a plan and how many app licenses you need, then pay securely with Stripe. Each license covers one web app. New licenses appear below once payment is confirmed."
                maxWidthPx={260}
              />
            </div>

            {plansLoading ? (
              <p className="text-sm text-gray-500">Loading plans…</p>
            ) : plansError ? (
              <p className="text-sm text-red-600">{plansError}</p>
            ) : purchasablePlans.length === 0 ? (
              <p className="text-sm text-gray-500">
                No plans are available to purchase right now.
              </p>
            ) : (
              <div className="divide-y divide-gray-100">
                {purchasablePlans.map((plan) => {
                  const quantity = quantityFor(plan.planId);
                  const perAppPrice = formatPerAppPrice(plan);
                  const isAnnual = plan.interval === "year";
                  const presentation = planPresentation(plan);
                  // Savings clause is appended rather than baked into the
                  // description so the figure stays live.
                  const description =
                    isAnnual && annualSavingsLabel
                      ? `${presentation.description} Save ${annualSavingsLabel} vs monthly.`
                      : presentation.description;
                  // Always shown, at every quantity. The first time a user meets
                  // the amount should not be on Stripe's card form.
                  const orderSummary = formatOrderSummary(plan, quantity);
                  // No price means Stripe could not be reached or the plan has
                  // no price behind it. Buying is blocked rather than sending
                  // the user to a card form for an amount we could not state.
                  const canBuy = perAppPrice !== null;
                  return (
                    <div
                      key={plan.planId}
                      className="flex items-center justify-between gap-4 py-3"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                          <p className="text-sm font-medium text-gray-900">
                            {presentation.label}
                          </p>
                          {/* Stated per web app, because that is the unit being
                              bought. A price is never zeroed or placeholdered
                              when the BE could not resolve it: quoting a price
                              we might not honor is worse than quoting none. */}
                          {perAppPrice ? (
                            <span className="text-sm font-semibold text-gray-900 tabular-nums">
                              {perAppPrice}
                            </span>
                          ) : (
                            <span className="text-xs text-amber-600">
                              Price unavailable
                            </span>
                          )}
                          {isAnnual && annualSavingsLabel && (
                            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                              Save {annualSavingsLabel}
                            </span>
                          )}
                        </div>
                        {/* Wording mirrors the sign-up portal word for word so
                            the two surfaces describe the same product the same
                            way. See stripeCheckout.service planPresentation. */}
                        <p className="mt-0.5 text-xs text-gray-500">{description}</p>
                        {orderSummary && (
                          <p className="mt-1 text-xs font-medium text-gray-700 tabular-nums">
                            {orderSummary}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {/* Stepper rather than a free-text number field: the only
                            reachable values are whole numbers within the BE's
                            accepted range, so 0, a negative, or a decimal can't
                            be entered at all. The BE still validates. */}
                        <div
                          className="flex items-center rounded-md border border-gray-200"
                          role="group"
                          aria-label={`Quantity for ${presentation.label}`}
                        >
                          <button
                            type="button"
                            onClick={() => setQuantityFor(plan.planId, quantity - 1)}
                            disabled={!canBuy || quantity <= MIN_PURCHASE_QUANTITY}
                            aria-label="Decrease quantity"
                            className="px-2 py-1 text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span
                            aria-live="polite"
                            className="min-w-[2rem] px-1 text-center text-sm tabular-nums text-gray-900"
                          >
                            {quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => setQuantityFor(plan.planId, quantity + 1)}
                            disabled={!canBuy || quantity >= MAX_PURCHASE_QUANTITY}
                            aria-label="Increase quantity"
                            className="px-2 py-1 text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleBuy(plan.planId, quantity)}
                          disabled={!canBuy || buyingPlanId !== null}
                          className="gap-1.5"
                        >
                          {buyingPlanId === plan.planId ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
                          ) : (
                            "Buy"
                          )}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {buyError && (
              <p className="mt-3 text-sm text-red-600" role="alert">{buyError}</p>
            )}
          </CardContent>
        </Card>

        {error && (
          <div
            role="alert"
            className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {error}
          </div>
        )}

        {/* Payment method — above the subscriptions it pays for. Only rendered
            once a billing relationship exists; a tenant that has never bought
            anything has no card and no use for this box. */}
        {overview?.canManagePaymentMethod && (cardLabel || overview?.nextChargeAt) && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-gray-400" />
                <h2 className="text-sm font-semibold text-gray-900">Payment method</h2>
                <HelpTip
                  label="How card details are handled"
                  content="Card details are entered directly with Stripe and never reach NIMIS. We store only the card type and last four digits so you can tell which card is on file."
                  maxWidthPx={280}
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="min-w-0">
                  {cardLabel ? (
                    <>
                      <p className="text-sm font-medium text-gray-900 tabular-nums">
                        {cardLabel}
                      </p>
                      {cardExpiry && (
                        <p
                          className={`mt-0.5 text-xs ${
                            cardExpiringSoon ? "font-medium text-amber-600" : "text-gray-500"
                          }`}
                        >
                          {cardExpiry}
                          {/* Warned BEFORE the charge fails: recovering a failed
                              renewal on a plan this size costs far more than a
                              line of copy. */}
                          {cardExpiringSoon && " — update it to avoid a failed renewal"}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">
                      No card on file. Add one so renewals don't fail.
                    </p>
                  )}
                  {overview?.nextChargeAt && (
                    <p className="mt-1 text-xs text-gray-500">
                      Next charge {formatShortDate(overview.nextChargeAt)}
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleUpdateCard}
                  disabled={cardSetupPending}
                  className="gap-1.5"
                >
                  {cardSetupPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
                  ) : cardLabel ? (
                    "Update card"
                  ) : (
                    "Add card"
                  )}
                </Button>
              </div>

              {cardError && (
                <p className="mt-3 text-sm text-red-600" role="alert">
                  {cardError}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Your subscriptions — one row per PURCHASE, because that is the unit
            Stripe bills and therefore the unit that can be cancelled. Sits above
            the licence lists: it answers "what am I paying for, and when does
            the next charge land", which is the question that brings an owner to
            this page. */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="mb-4 flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-gray-400" />
              <h2 className="text-sm font-semibold text-gray-900">Your subscriptions</h2>
              <HelpTip
                label="What this shows"
                content="Each purchase you've made, the licenses it covers, and when it renews. Cancelling takes effect at the end of the period you've already paid for, and can be undone until then."
                maxWidthPx={300}
              />
            </div>

            {changeNotice && (
              <div
                role="status"
                className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800"
              >
                {changeNotice}
              </div>
            )}

            {subscriptionsError && (
              <p className="mb-3 text-sm text-red-600" role="alert">
                {subscriptionsError}
              </p>
            )}

            {subscriptionsLoading && subscriptionGroups.length === 0 && (
              <p className="text-sm text-gray-500">Loading subscriptions…</p>
            )}

            {!subscriptionsLoading && subscriptionGroups.length === 0 && !subscriptionsError && (
              <p className="text-sm text-gray-500">
                No active subscriptions. Point-in-Time licenses are one-off purchases
                and don't renew.
              </p>
            )}

            {subscriptionGroups.length > 0 && (
              <div className="divide-y divide-gray-100">
                {subscriptionGroups.map((group) => {
                  const matchingPlan = plans.find((plan) => plan.planId === group.planId);
                  const planLabel = matchingPlan
                    ? planPresentation(matchingPlan).label
                    : "Continuous";
                  // Either flavour of pending change: the whole subscription
                  // ending, or a scheduled reduction.
                  const isEnding = group.cancelAtPeriodEnd;
                  const hasPendingReduction =
                    !isEnding && group.pendingCancelCount > 0;
                  const pendingDate = group.cancelsAt || group.currentPeriodEnd;
                  return (
                    <div key={group.licenseGroupId} className="py-3">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {planLabel} · {group.quantity} license
                            {group.quantity === 1 ? "" : "s"}
                          </p>
                          <p className="mt-0.5 text-xs text-gray-500">
                            {group.unassignedCount > 0
                              ? `${group.quantity - group.unassignedCount} assigned to apps · ${group.unassignedCount} unassigned`
                              : `Covering ${group.quantity} app${group.quantity === 1 ? "" : "s"}`}
                          </p>
                          {/* Says "renews" here — unlike the licence rows, this
                              IS the charge date. */}
                          {!isEnding && !hasPendingReduction && group.currentPeriodEnd && (
                            <p className="mt-0.5 text-xs text-gray-500">
                              Renews {formatShortDate(group.currentPeriodEnd)}
                            </p>
                          )}
                          {isEnding && (
                            <p className="mt-0.5 text-xs font-medium text-amber-600">
                              Cancels{pendingDate ? ` on ${formatShortDate(pendingDate)}` : ""} —
                              everything keeps working until then
                            </p>
                          )}
                          {hasPendingReduction && (
                            <p className="mt-0.5 text-xs font-medium text-amber-600">
                              {group.pendingCancelCount} license
                              {group.pendingCancelCount === 1 ? "" : "s"} end
                              {pendingDate ? ` on ${formatShortDate(pendingDate)}` : ""}
                            </p>
                          )}
                        </div>
                        <div className="shrink-0">
                          {!group.canCancel ? (
                            // Enterprise: contracted offline, so there is no
                            // self-serve change to offer.
                            <span className="text-xs text-gray-400">
                              Managed by your account manager
                            </span>
                          ) : isEnding || hasPendingReduction ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={resumingGroupId !== null}
                              onClick={() => handleResumeSubscription(group)}
                              className="gap-1.5"
                            >
                              {resumingGroupId === group.licenseGroupId ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
                              ) : (
                                "Undo cancellation"
                              )}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setCancelError(null);
                                setChangeNotice(null);
                                setCancelTarget(group);
                              }}
                            >
                              Cancel licenses
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Available licenses — bought, waiting to be attached to an app.
            Summarized by plan rather than listed one row per license: ten spare
            licenses of the same plan are one fact ("ten available"), not ten
            facts, and echoing the Buy box's own plan names keeps the two
            readable against each other.

            Sits ABOVE Licensed apps because it is the actionable box: it tells
            the owner whether they can add another app right now. */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="mb-4 flex items-center gap-2">
              <Ticket className="h-4 w-4 text-gray-400" />
              <h2 className="text-sm font-semibold text-gray-900">Available licenses</h2>
              <HelpTip
                label="What this shows"
                content="Licenses you've paid for that aren't attached to an app yet. You choose one when you add a web app."
                maxWidthPx={260}
              />
            </div>

            {licensesPending && <p className="text-sm text-gray-500">Loading licenses…</p>}

            {!licensesPending && availableLicenseGroups.length === 0 && (
              <p className="text-sm text-gray-500">
                No spare licenses. Buy one above to add another web app.
              </p>
            )}

            {availableLicenseGroups.length > 0 && (
              <div className="divide-y divide-gray-100">
                {availableLicenseGroups.map((group) => (
                  <div
                    key={group.key}
                    className="flex items-center justify-between gap-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">{group.label}</p>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {group.testsAllowed} pentest
                        {group.testsAllowed === 1 ? "" : "s"} per period once attached
                      </p>
                    </div>
                    <p className="shrink-0 text-sm font-semibold text-gray-900 tabular-nums">
                      {group.count} available
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Licensed apps — a license and the app it covers. */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="mb-4 flex items-center gap-2">
              <Ticket className="h-4 w-4 text-gray-400" />
              <h2 className="text-sm font-semibold text-gray-900">Licensed apps</h2>
              <HelpTip
                label="What this shows"
                content="Each web app that has a license attached, with the pentests left in the current period."
                maxWidthPx={260}
              />
            </div>

            {licensesPending && <p className="text-sm text-gray-500">Loading licenses…</p>}

            {!licensesPending && licensedApps.length === 0 && (
              <p className="text-sm text-gray-500">
                No apps are licensed yet. Buy a license above, then attach it when you
                add an app.
              </p>
            )}

            {licensedApps.length > 0 && (
              <div className="divide-y divide-gray-100">
                {licensedApps.map((entitlement) => {
                  const status = licenseStatusLabel(entitlement);
                  const appName =
                    appNameById.get(entitlement.boundWebAppId as string) || "Assigned app";
                  return (
                    <div
                      key={entitlement.id}
                      className="flex items-center justify-between gap-4 py-3"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {appName}
                        </p>
                        <p className="mt-0.5 text-xs text-gray-500">
                          {allowanceLabel(entitlement)}
                        </p>
                      </div>
                      <p className={`shrink-0 text-xs text-right ${TONE_CLASS[status.tone]}`}>
                        {status.text}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Apps with no LIVE license. Two ways to get here: a license that
            lapsed when its subscription was cancelled, or (rarely) a tenant
            still being backfilled. Both are fixed the same way — attach a spare
            license — so this box is an action list, not a warning. It used to
            say "contact support", which was the only honest advice before
            licenses could be attached to an existing app. */}
        {appsWithoutLicense.length > 0 && (
          <Card>
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <h2 className="text-sm font-semibold text-gray-900">
                  Apps without a license
                </h2>
                <HelpTip
                  label="What this shows"
                  content="Apps that can't run pentests because no live license covers them. Attach one of your available licenses to put an app back in service."
                  maxWidthPx={280}
                />
              </div>
              <p className="mb-3 text-xs text-gray-500">
                {availableLicenseGroups.length > 0
                  ? "Pentests can't run on these apps. Attach one of your available licenses to put an app back in service."
                  : "Pentests can't run on these apps. Buy a license above, then attach it here."}
              </p>
              <div className="divide-y divide-gray-100">
                {appsWithoutLicense.map((app) => {
                  const appId = app.id as string;
                  // Default to the first group so the common single-plan case is
                  // one click with nothing to choose.
                  const chosenKey =
                    chosenLicenseKeyByAppId[appId] ?? availableLicenseGroups[0]?.key;
                  const chosenGroup =
                    availableLicenseGroups.find((group) => group.key === chosenKey) ??
                    availableLicenseGroups[0];
                  const canAssign = Boolean(chosenGroup) && assigningAppId === null;
                  const assignError = assignErrorByAppId[appId];
                  return (
                    <div key={appId} className="py-3">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex min-w-0 items-center gap-3">
                          <Globe className="h-4 w-4 shrink-0 text-gray-300" />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-gray-900">
                              {app.name || appId}
                            </p>
                            {app.url && (
                              <p className="truncate text-xs text-gray-500">{app.url}</p>
                            )}
                            {/* Says which of the two situations this is. An owner
                                who just cancelled needs to know this app is a
                                consequence of that, not a separate fault. */}
                            <p className="mt-0.5 text-xs text-amber-600">
                              {lapsedAppIds.has(appId)
                                ? "Its license has ended"
                                : "No license attached"}
                            </p>
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {/* Only a choice when there IS one. */}
                          {availableLicenseGroups.length > 1 && (
                            <select
                              value={chosenKey}
                              onChange={(selectEvent) =>
                                setChosenLicenseKeyByAppId((previous) => ({
                                  ...previous,
                                  [appId]: selectEvent.target.value,
                                }))
                              }
                              aria-label={`License to attach to ${app.name || appId}`}
                              className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700"
                            >
                              {availableLicenseGroups.map((group) => (
                                <option key={group.key} value={group.key}>
                                  {group.label} ({group.count})
                                </option>
                              ))}
                            </select>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!canAssign}
                            onClick={() =>
                              handleAssignLicense(
                                appId,
                                chosenGroup?.entitlementIds[0] ?? null
                              )
                            }
                            className="gap-1.5"
                          >
                            {assigningAppId === appId ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
                            ) : (
                              "Attach license"
                            )}
                          </Button>
                        </div>
                      </div>
                      {assignError && (
                        <p className="mt-2 text-xs text-red-600" role="alert">
                          {assignError}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Invoices — last, because it is the reference section rather than an
            action. These are the same receipts already emailed on every
            payment; this is the "I lost the email" path, and it keeps receipt
            retrieval in the dashboard instead of a Stripe-hosted portal. The
            documents themselves are Stripe's, which is correct — they are the
            issued tax document, not something to re-render here. */}
        {(invoicesLoading || invoices.length > 0 || invoicesError) && (
          <Card className="mt-6">
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <Receipt className="h-4 w-4 text-gray-400" />
                <h2 className="text-sm font-semibold text-gray-900">Invoices</h2>
                <HelpTip
                  label="What this shows"
                  content="Every invoice raised on your account. We also email each one when it's paid. Open or download any of them for your records."
                  maxWidthPx={280}
                />
              </div>

              {invoicesError && (
                <p className="text-sm text-red-600" role="alert">{invoicesError}</p>
              )}

              {invoicesLoading && !invoicesError && (
                <p className="text-sm text-gray-500">Loading invoices…</p>
              )}

              {!invoicesLoading && !invoicesError && invoices.length === 0 && (
                <p className="text-sm text-gray-500">No invoices yet.</p>
              )}

              {invoices.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[32rem] text-sm">
                    <caption className="sr-only">Invoices raised on your account</caption>
                    <thead>
                      <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                        <th scope="col" className="py-2 pr-4 font-medium">Date</th>
                        <th scope="col" className="py-2 pr-4 font-medium">Invoice</th>
                        <th scope="col" className="py-2 pr-4 text-right font-medium">Amount</th>
                        <th scope="col" className="py-2 pr-4 font-medium">Status</th>
                        <th scope="col" className="py-2 text-right font-medium">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {invoices.map((invoice) => {
                        const isPaid = invoice.status === "paid";
                        return (
                          <tr key={invoice.invoiceId || invoice.number}>
                            <td className="py-2.5 pr-4 text-gray-700 tabular-nums">
                              {invoice.createdAt ? formatShortDate(invoice.createdAt) : "—"}
                            </td>
                            <td className="py-2.5 pr-4 text-gray-700">
                              {invoice.number || "—"}
                            </td>
                            <td className="py-2.5 pr-4 text-right text-gray-900 tabular-nums">
                              {invoice.amountPaid != null && invoice.currency
                                ? formatPlanAmount(invoice.amountPaid, invoice.currency)
                                : "—"}
                            </td>
                            <td className="py-2.5 pr-4">
                              {/* An unpaid invoice is the one row an owner must
                                  act on, so it is coloured rather than left to
                                  blend in with the paid ones. */}
                              <span
                                className={`text-xs ${
                                  isPaid ? "text-gray-500" : "font-medium text-amber-600"
                                }`}
                              >
                                {isPaid ? "Paid" : invoice.status === "open" ? "Due" : "Unpaid"}
                              </span>
                            </td>
                            <td className="py-2.5 text-right">
                              <div className="flex items-center justify-end gap-3">
                                {invoice.hostedInvoiceUrl && (
                                  <a
                                    href={invoice.hostedInvoiceUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                    View
                                  </a>
                                )}
                                {invoice.invoicePdf && (
                                  <a
                                    href={invoice.invoicePdf}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                  >
                                    <Download className="h-3 w-3" />
                                    PDF
                                  </a>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

      </div>

      {cardSetup && (
        <BuyLicensesModal
          clientSecret={cardSetup.clientSecret}
          publishableKey={cardSetup.publishableKey}
          ariaLabel="Update payment method"
          completedTitle="Card saved"
          completedMessage="Future charges will use this card. It may take a moment to appear above."
          onClose={() => {
            setCardSetup(null);
            // Closing isn't proof they didn't save a card — re-read rather than
            // assume, the same reason the buy flow re-reads on close.
            loadOverview();
          }}
          onComplete={handleCardSetupComplete}
        />
      )}

      {cancelTarget && (
        <CancelLicensesModal
          group={cancelTarget}
          planLabel={
            (() => {
              const matchingPlan = plans.find((plan) => plan.planId === cancelTarget.planId);
              return matchingPlan ? planPresentation(matchingPlan).label : "Continuous";
            })()
          }
          entitlements={entitlements}
          appNameById={appNameById}
          wouldCloseAccountIfAllCancelled={wouldCloseAccountIfGroupFullyCancelled(cancelTarget)}
          // From the backend, which reads the SAME SSM parameter the purge job
          // uses — never hardcoded here, so the window promised at closure
          // cannot drift from the one actually applied. Null (overview read
          // failed) renders "a grace period" rather than an invented number.
          dataRetentionDays={overview?.dataRetentionDays ?? null}
          isSubmitting={cancelSubmitting}
          errorMessage={cancelError}
          onCancelLicenses={handleCancelLicenses}
          onClose={() => {
            setCancelTarget(null);
            setCancelError(null);
          }}
        />
      )}

      {checkout && (
        <BuyLicensesModal
          clientSecret={checkout.clientSecret}
          publishableKey={checkout.publishableKey}
          onClose={handleCheckoutClose}
          onComplete={handleCheckoutComplete}
        />
      )}
    </div>
  );
}
